import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { parseDbTimestamp, type NotificationDTO } from '@wf/shared'
import { formatDateTimeDE } from '../time/format'
import { isPushSupported, pushPermission, subscribePush } from './push'
import { useMarkNotificationsRead, useNotifications } from './useNotifications'

/**
 * Benachrichtigungs-Glocke der Kopfleiste (In-App-Zentrale). Ungelesen-Badge am Glockensymbol; Klick
 * öffnet ein Popover mit der Liste. Ein Klick auf eine Benachrichtigung markiert sie gelesen und springt
 * zum Ziel; „Alle als gelesen" leert den Badge. Zweiter Zustellkanal (OS-Push) hängt später an derselben
 * Datenquelle (docs/DESIGN-benachrichtigungen.md).
 *
 * A11y wie ProfileMenu/NavMenu: Disclosure-Pattern (Button mit aria-expanded + echte Bedienelemente),
 * KEIN ARIA-Menü. Schließt bei Klick außerhalb (pointerdown) und Escape.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { data } = useNotifications()
  const markRead = useMarkNotificationsRead()
  const qc = useQueryClient()

  const items = data?.items ?? []
  const unread = data?.unreadTotal ?? 0

  // Geteiltes Tablet: beim Mounten (Login) das Gerät still dem AKTUELLEN Konto zuordnen, falls Push schon erlaubt.
  useEffect(() => {
    if (pushPermission() === 'granted') void subscribePush(false)
  }, [])

  // Trifft ein Push ein, während die App offen ist → der SW meldet es per postMessage; Zentrale neu laden.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMsg = (e: MessageEvent): void => {
      if (e.data && e.data.type === 'notifications-updated') void qc.invalidateQueries({ queryKey: ['notifications'] })
    }
    navigator.serviceWorker.addEventListener('message', onMsg)
    return () => navigator.serviceWorker.removeEventListener('message', onMsg)
  }, [qc])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const onItem = (n: NotificationDTO): void => {
    setOpen(false)
    if (!n.readAt) markRead.mutate({ ids: [n.id] })
    if (n.target) navigate(n.target)
  }
  const onReadAll = (): void => {
    if (unread > 0) markRead.mutate({ all: true })
  }

  return (
    <div className="wf-notif" ref={ref}>
      <button
        type="button"
        className="wf-notif__btn"
        aria-expanded={open}
        aria-label={unread > 0 ? `Benachrichtigungen, ${unread} ungelesen` : 'Benachrichtigungen'}
        onClick={() => setOpen((v) => !v)}
      >
        <IconBell />
        {unread > 0 ? (
          <span className="wf-notif__badge" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="wf-notif__pop" aria-label="Benachrichtigungen">
          <div className="wf-notif__head">
            <span className="wf-notif__title">Benachrichtigungen</span>
            {unread > 0 ? (
              <button type="button" className="wf-notif__readall" onClick={onReadAll} disabled={markRead.isPending}>
                Alle als gelesen
              </button>
            ) : null}
          </div>
          <PushOptIn />
          {items.length === 0 ? (
            <p className="wf-notif__empty">Keine Benachrichtigungen.</p>
          ) : (
            <ul className="wf-notif__list">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={n.readAt ? 'wf-notif__item' : 'wf-notif__item wf-notif__item--unread'}
                    onClick={() => onItem(n)}
                  >
                    <span className={n.readAt ? 'wf-notif__dot wf-notif__dot--read' : 'wf-notif__dot'} aria-hidden="true" />
                    <span className="wf-notif__itemtext">
                      <span className="wf-notif__itemtitle">{n.title}</span>
                      {n.body ? <span className="wf-notif__itembody">{n.body}</span> : null}
                      <span className="wf-notif__itemtime">{formatDateTimeDE(parseDbTimestamp(n.createdAt))}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Kompakter Opt-in-Streifen im Popover: bietet OS-Push an, solange der Nutzer noch nicht entschieden hat
 *  (Notification.permission === 'default'). Nach Zusage/Ablehnung verschwindet er (kein Nörgeln). */
function PushOptIn() {
  const [status, setStatus] = useState<'prompt' | 'working' | 'hidden'>(() =>
    isPushSupported() && pushPermission() === 'default' ? 'prompt' : 'hidden',
  )
  if (status === 'hidden') return null
  const enable = async (): Promise<void> => {
    setStatus('working')
    await subscribePush(true)
    setStatus('hidden')
  }
  return (
    <div className="wf-notif__optin">
      <span className="wf-notif__optintext">Auch benachrichtigt werden, wenn die App geschlossen ist?</span>
      <button type="button" className="wf-btn wf-btn--sm" onClick={enable} disabled={status === 'working'}>
        {status === 'working' ? '…' : 'Push aktivieren'}
      </button>
    </div>
  )
}

/* Icon: Lucide „bell", inline (harte CSP — keine Icon-CDNs/Pakete; Konvention wie ProfileMenu). */
function IconBell() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  )
}
